/* eslint-disable no-console */
import { useState, useEffect, useMemo } from "react";
import {
  client,
  getClientes,
  getContratos,
  getDepreciacionYAmortizacion,
  getEdificios,
  getIntermediarios,
  getOportunidades,
  getUsers,
  getEmpleados,
  getRemuneracionesEmpleado,
  getInversionMensual,
  getValorActivosMensual,
  getCostosFijos,
  getCostosHardwareFlex,
  getCostosPublicidad,
} from "../../../utils";
import { cargarSnapshotComision, cargarSnapshotPctCongelado } from "../../../utils/comisionSnapshot";
import { cargarCostosIntermediacion } from "../../../utils/costosIntermediacion";
import { loadExchangeRates } from "../../../utils/exchangeRateService";
import { INICIO_COSTOS_SII } from "../costosER";

const PAISES = ["Chile", "Perú"];
// Mismos campos que ER/constants.js:21 (se duplica la lista para no acoplar el hook
// de datos a las constantes de presentacion del v1).
const CAMPOS_COSTOS_FIJOS = [
  "oficina",
  "transporte",
  "telefonia",
  "marketing",
  "contador",
  "abogados",
  "gastos_representacion",
  "asesoria_externa",
  "alojamiento",
  "viajes_gerencia",
  "gastos_bancarios",
  "varios",
];
const CERO12 = () => Array(12).fill(0);
// Referencia estable: devolver `[]` nuevo en cada render invalidaría los memos del motor.
const VACIO = [];

const porId = (rows) =>
  (rows || []).reduce((acc, r) => {
    if (r.id) acc[r.id] = r;
    return acc;
  }, {});

const clientesPorId = (rows) =>
  (rows || []).reduce((acc, c) => {
    // El v1 normaliza `agencia` vacía a null; se replica igual.
    if (c.id) acc[c.id] = { ...c, agencia: c.agencia || null };
    return acc;
  }, {});

/**
 * Carga de datos del ER v2 — AMBOS PAÍSES, igual que el v1.
 *
 * Replica los efectos de carga de src/pages/ER/index.js. Devuelve los datos con el
 * mismo desdoblamiento `*Chile` / `*Peru` que consumen los hooks de cálculo cuando
 * `modoCombinado` está activo, más los alias del país de sesión.
 *
 * `costos_gestion_contenidos` se carga aunque sea tabla de costos manuales: aporta la
 * `fecha_factura` con la que los bloques B y C reconocen el INGRESO del mes.
 *
 * @param {string} pais - País de la sesión
 * @param {number} selectedYear - Año seleccionado
 */
export const useERv2Data = (pais, selectedYear) => {
  const [porPais, setPorPais] = useState({});
  const [users, setUsers] = useState({});
  const [costosGestionData, setCostosGestionData] = useState([]);
  const [depAmort, setDepAmort] = useState({});
  // Tablas de costo MANUAL del v1. Alimentan los meses anteriores a 2026-01-01, donde
  // el motor de facturas no aplica (ver costosER.js).
  const [manual, setManual] = useState({});
  const [inversionPorPais, setInversionPorPais] = useState({});
  const [valorActivosPorPais, setValorActivosPorPais] = useState({});
  // Las tres fuentes de costo por documento se guardan POR PAÍS, no solo las de la sesión.
  // El modo combinado necesita la pata de Chile aunque la sesión sea Perú: `pais` sale del
  // HOSTNAME (visiona.pe → Perú, wecast.cl → Chile; ver context/auth.js), así que filtrar la
  // query por él hacía desaparecer las facturas de Chile del agregado de región.
  const [facturasSIIPorPais, setFacturasSIIPorPais] = useState({});
  const [facturasExternasPorPais, setFacturasExternasPorPais] = useState({});
  const [boletasHonorariosPorPais, setBoletasHonorariosPorPais] = useState({});
  // La porción de la sesión, para la vista de país único. Se deriva de los mapas por país en
  // vez de guardarse aparte: un solo origen de verdad, sin riesgo de que queden desfasados.
  const facturasSII = useMemo(() => facturasSIIPorPais[pais] || VACIO, [facturasSIIPorPais, pais]);
  const facturasExternas = useMemo(() => facturasExternasPorPais[pais] || VACIO, [facturasExternasPorPais, pais]);
  const boletasHonorarios = useMemo(() => boletasHonorariosPorPais[pais] || VACIO, [boletasHonorariosPorPais, pais]);

  const [empleadosPorPais, setEmpleadosPorPais] = useState({});
  const [dtesSinVincular, setDtesSinVincular] = useState(0);
  const [siiVinculacionesMap, setSiiVinculacionesMap] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ─── Ventas, contratos y catálogos de AMBOS países (no dependen del año) ───
  useEffect(() => {
    let cancelado = false;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        await loadExchangeRates();

        const porPaisData = {};
        await Promise.all(
          PAISES.map(async (p) => {
            const [opps, ctrs, cli, inter, edif, snap, costos, pct, negocios, btl] = await Promise.all([
              getOportunidades({ pais: p }).catch(() => []),
              getContratos({ pais: p }).catch(() => []),
              getClientes({ pais: p }).catch(() => []),
              getIntermediarios({ pais: p }).catch(() => []),
              getEdificios({ pais: p }).catch(() => []),
              cargarSnapshotComision(p).catch(() => null),
              cargarCostosIntermediacion(p).catch(() => null),
              cargarSnapshotPctCongelado(p).catch(() => null),
              client.from("negocios_carteleria").select("*").eq("pais", p).neq("eliminada", true),
              client.from("sales_btl").select("*").eq("pais", p).eq("eliminada", false),
            ]);

            porPaisData[p] = {
              oportunidades: (opps || []).filter((o) => o.pais === p),
              contratos: ctrs || [],
              clientes: clientesPorId(cli),
              intermediarios: porId(inter),
              edificios: porId(edif),
              snapMap: snap,
              costosMap: costos,
              pctMap: pct,
              negociosCarteleria: negocios.data || [],
              salesBTL: btl.data || [],
            };
          })
        );

        const usuarios = await getUsers().catch(() => []);

        if (cancelado) return;
        setPorPais(porPaisData);
        setUsers(porId(usuarios));
      } catch (err) {
        console.error("ERv2: error cargando datos base", err);
      } finally {
        if (!cancelado) setIsLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelado = true;
    };
  }, []);

  // ─── Datos dependientes del año ──────────────────────────────
  useEffect(() => {
    if (!pais || !selectedYear) return;
    let cancelado = false;

    const fetchAno = async () => {
      try {
        const [gestion, depChile, depPeru, sii, externas, honorarios, invCL, invPE, valCL, valPE] = await Promise.all([
          client.from("costos_gestion_contenidos").select("*").eq("pais", pais).eq("ano", selectedYear),
          getDepreciacionYAmortizacion("Chile", selectedYear),
          getDepreciacionYAmortizacion("Perú", selectedYear),
          // Fase 2: fuentes del costo automático. Se traen sin filtrar por año; el motor
          // filtra por fecha_emision para no depender de una columna de período.
          // Corte duro: el costo del SII arranca el 2026-01-01 (ver costosER.js).
          // Tampoco se TRAEN facturas anteriores, no solo se descartan al computar.
          // SIN filtro de país: se traen los dos y se parten acá. El combinado arma una pata
          // por país y la de Chile no puede depender de desde qué dominio se entró.
          client.from("facturas_sii_recibidas").select("*").gte("fecha_emision", INICIO_COSTOS_SII),
          client.from("facturas_externas").select("*").gte("fecha_emision", INICIO_COSTOS_SII),
          // Tercera fuente de costo: Boletas de Honorarios por documento. El filtro por
          // `computa` (vigentes) lo hace el motor, no la query — misma razón que el resto de
          // las reglas de negocio: viven en costosER.js, donde se testean.
          client.from("boletas_honorarios").select("*").gte("fecha_emision", INICIO_COSTOS_SII),
          // Checkbox "Activos": inversion y valor en libros por pais.
          getInversionMensual("Chile", selectedYear).catch(() => CERO12()),
          getInversionMensual("Perú", selectedYear).catch(() => CERO12()),
          getValorActivosMensual("Chile", selectedYear).catch(() => CERO12()),
          getValorActivosMensual("Perú", selectedYear).catch(() => CERO12()),
        ]);

        if (cancelado) return;
        setCostosGestionData(gestion.data || []);
        setDepAmort({ Chile: depChile, Perú: depPeru });
        const partirPorPais = (filas) => ({
          Chile: (filas || []).filter((f) => f.pais === "Chile"),
          Perú: (filas || []).filter((f) => f.pais === "Perú"),
        });
        setFacturasSIIPorPais(partirPorPais(sii.data));
        setFacturasExternasPorPais(partirPorPais(externas.data));
        setBoletasHonorariosPorPais(partirPorPais(honorarios.data));
        setInversionPorPais({ Chile: invCL || CERO12(), Perú: invPE || CERO12() });
        setValorActivosPorPais({ Chile: valCL || CERO12(), Perú: valPE || CERO12() });
      } catch (err) {
        console.error("ERv2: error cargando datos del año", err);
      }
    };

    fetchAno();
    return () => {
      cancelado = true;
    };
  }, [pais, selectedYear]);

  // ─── Tablas de costo manual (pre-2026) ──────────────────────
  useEffect(() => {
    if (!pais || !selectedYear) return;
    let cancelado = false;

    // Misma normalizacion que el v1 (ER/index.js:1026): un objeto por mes con todos
    // los campos, mas asesoria_externa_usd.
    const normFijos = (filas) =>
      Array.from({ length: 12 }, (_, i) => {
        const row = (filas || []).find((r) => Number(r.mes) === i + 1);
        const mesData = {};
        CAMPOS_COSTOS_FIJOS.forEach((campo) => {
          mesData[campo] = Number(row?.[campo]) || 0;
        });
        mesData.asesoria_externa_usd = Number(row?.asesoria_externa_usd) || 0;
        mesData.asesoria_externa_fecha = row?.asesoria_externa_fecha || null;
        return mesData;
      });

    // Publicidad: null = sin dato (el v1 proyecta), numero = valor real (incluso 0).
    const normPublicidad = (filas) =>
      Array.from({ length: 12 }, (_, i) => {
        const row = filas?.[i];
        if (!row) return null;
        return {
          servicios_internet: row.servicios_internet == null ? null : Number(row.servicios_internet),
          software_ds: row.software_ds == null ? null : Number(row.software_ds),
          mantenimiento: row.mantenimiento == null ? null : Number(row.mantenimiento),
        };
      });

    // Se cargan las tablas manuales de AMBOS paises: el modo combinado pre-2026
    // necesita los costos de los dos para armar los agregados de region.
    const cargarPais = async (p) => {
      const vacio = () => [];
      const [pub, pubAA, hw, hwAA, gest, gestAA, fijos, fijosAA] = await Promise.all([
        getCostosPublicidad({ pais: p, ano: selectedYear }).catch(vacio),
        getCostosPublicidad({ pais: p, ano: selectedYear - 1 }).catch(vacio),
        getCostosHardwareFlex({ pais: p, ano: selectedYear }).catch(vacio),
        getCostosHardwareFlex({ pais: p, ano: selectedYear - 1 }).catch(vacio),
        client.from("costos_gestion_contenidos").select("*").eq("pais", p).eq("ano", selectedYear),
        client
          .from("costos_gestion_contenidos")
          .select("*")
          .eq("pais", p)
          .eq("ano", selectedYear - 1),
        getCostosFijos({ pais: p, ano: selectedYear }).catch(vacio),
        getCostosFijos({ pais: p, ano: selectedYear - 1 }).catch(vacio),
      ]);
      return {
        publicidad: normPublicidad(pub),
        publicidadAA: normPublicidad(pubAA),
        hardwareFlex: hw || [],
        hardwareFlexAA: hwAA || [],
        gestion: gest.data || [],
        gestionAA: gestAA.data || [],
        rawFijos: fijos || [],
        fijos: normFijos(fijos),
        fijosAA: normFijos(fijosAA),
      };
    };

    const fetchManual = async () => {
      try {
        const porPaisManual = {};
        await Promise.all(
          PAISES.map(async (p) => {
            porPaisManual[p] = await cargarPais(p);
          })
        );
        if (cancelado) return;
        setManual({ ...porPaisManual[pais], porPais: porPaisManual });
      } catch (err) {
        console.error("ERv2: error cargando tablas de costo manual", err);
      }
    };

    fetchManual();
    return () => {
      cancelado = true;
    };
  }, [pais, selectedYear]);

  // ─── Empleados de AMBOS países (el modo combinado necesita el RRHH de los dos) ───
  useEffect(() => {
    let cancelado = false;
    const fetchEmpleadosAmbos = async () => {
      try {
        const resultado = {};
        await Promise.all(
          PAISES.map(async (p) => {
            const base = await getEmpleados(p).catch(() => []);
            resultado[p] = await Promise.all(
              (base || []).map(async (emp) => ({
                ...emp,
                remuneraciones: (await getRemuneracionesEmpleado(emp.id).catch(() => [])) || [],
              }))
            );
          })
        );
        if (!cancelado) setEmpleadosPorPais(resultado);
      } catch (err) {
        console.error("ERv2: error cargando empleados de ambos países", err);
      }
    };
    fetchEmpleadosAmbos();
    return () => {
      cancelado = true;
    };
  }, []);

  // ─── Costo del SII SIN CLASIFICAR (cobertura de cuenta_er) ───
  // ANTES contaba `estado_vinculacion='pendiente'` de emitidas + recibidas. Eso mide el eje
  // de PAGO, no el de clasificación: sobre-contaba las pendientes YA clasificadas (que sí
  // devengan) y OMITÍA las 'vinculada' sin cuenta_er — el costo que de verdad no entra al ER
  // (128 facturas, ~$40M invisibles). Es la misma descontaminación de la capa de Compras
  // (regla 10.65): el warning ahora mide lo que le FALTA al ER, no el flag sobrecargado.
  //
  // Solo RECIBIDAS: las emitidas son ventas, no se clasifican con cuenta_er. Se excluyen las
  // NC (61, no son costo) y los activos (tipo_contable='activo', van a Depreciación), igual
  // que el motor `costosER`. `cuenta_er` vacío ('') cuenta como sin clasificar, como en costosER.
  useEffect(() => {
    if (!pais || pais === "Region") return;
    let cancelado = false;

    const fetchDTEs = async () => {
      try {
        const desde = `${selectedYear}-01-01`;
        const hasta = `${selectedYear}-12-31`;
        const recRes = await client
          .from("facturas_sii_recibidas")
          .select("id, cuenta_er, tipo_contable")
          .eq("pais", pais)
          .neq("tipo_dte", 61)
          .gte("fecha_emision", desde)
          .lte("fecha_emision", hasta);
        const sinClasificar = (recRes.data || []).filter((f) => !f.cuenta_er && f.tipo_contable !== "activo").length;
        if (!cancelado) setDtesSinVincular(sinClasificar);
      } catch {
        /* silenciar si las tablas no existen aun */
      }
    };

    const fetchVinculaciones = async () => {
      try {
        const { data } = await client
          .from("facturas_sii_emitidas")
          .select("folio, id, facturas_sii_vinculaciones(venta_tipo, venta_ref_id, venta_ref_index)")
          .eq("pais", pais)
          .eq("estado_vinculacion", "vinculada");
        const map = { _byRef: {} };
        (data || []).forEach((f) => {
          map[String(f.folio)] = { facturaId: f.id };
          (f.facturas_sii_vinculaciones || []).forEach((v) => {
            const key = `${v.venta_tipo}:${v.venta_ref_id}:${v.venta_ref_index ?? ""}`;
            map._byRef[key] = { facturaId: f.id, folio: f.folio };
          });
        });
        if (!cancelado) setSiiVinculacionesMap(map);
      } catch {
        if (!cancelado) setSiiVinculacionesMap({ _byRef: {} });
      }
    };

    fetchDTEs();
    fetchVinculaciones();
    return () => {
      cancelado = true;
    };
  }, [pais, selectedYear]);

  const chile = porPais.Chile || {};
  const peru = porPais["Perú"] || {};
  const sesion = porPais[pais] || {};
  const otro = pais === "Chile" ? peru : chile;

  // Los hooks de BTL filtran por año, pero necesitan el set del año anterior aparte.
  const btlDelAno = (filas, ano) =>
    (filas || []).filter((r) => r.facturafecha && new Date(r.facturafecha).getFullYear() === ano);

  return {
    // País de sesión (alias, como en el v1)
    oportunidades: sesion.oportunidades || [],
    contratos: sesion.contratos || [],
    clientes: sesion.clientes || {},
    intermediarios: sesion.intermediarios || {},
    edificios: sesion.edificios || {},
    snapMap: sesion.snapMap || null,
    pctMap: sesion.pctMap || null,
    costosMap: sesion.costosMap || null,
    negociosCarteleria: sesion.negociosCarteleria || [],
    salesBTL: btlDelAno(sesion.salesBTL, selectedYear),
    salesBTLAnoAnterior: btlDelAno(sesion.salesBTL, selectedYear - 1),
    salesBTLOtroPais: btlDelAno(otro.salesBTL, selectedYear),
    // Desdoblado por país (modo combinado)
    oportunidadesChile: chile.oportunidades || [],
    oportunidadesPeru: peru.oportunidades || [],
    contratosChile: chile.contratos || [],
    contratosPeru: peru.contratos || [],
    clientesChile: chile.clientes || {},
    clientesPeru: peru.clientes || {},
    intermediariosChile: chile.intermediarios || {},
    intermediariosPeru: peru.intermediarios || {},
    edificiosChile: chile.edificios || {},
    edificiosPeru: peru.edificios || {},
    snapMapChile: chile.snapMap || null,
    snapMapPeru: peru.snapMap || null,
    pctMapChile: chile.pctMap || null,
    pctMapPeru: peru.pctMap || null,
    costosMapChile: chile.costosMap || null,
    costosMapPeru: peru.costosMap || null,
    negociosCarteleriaChile: chile.negociosCarteleria || [],
    negociosCarteleriaPeru: peru.negociosCarteleria || [],
    salesBTLChile: btlDelAno(chile.salesBTL, selectedYear),
    salesBTLPeru: btlDelAno(peru.salesBTL, selectedYear),
    // Comunes
    users,
    costosGestionData,
    depreciacionMensual: depAmort[pais]?.depreciacion || CERO12(),
    amortizacionMensual: depAmort[pais]?.amortizacion || CERO12(),
    depreciacionMensualChile: depAmort.Chile?.depreciacion || CERO12(),
    depreciacionMensualPeru: depAmort["Perú"]?.depreciacion || CERO12(),
    amortizacionMensualChile: depAmort.Chile?.amortizacion || CERO12(),
    amortizacionMensualPeru: depAmort["Perú"]?.amortizacion || CERO12(),
    // Vista de país único: la porción de la sesión (shape intacto para sus consumidores).
    facturasSII,
    facturasExternas,
    boletasHonorarios,
    // Vista de región: cada pata del combinado toma la suya, sin depender de la sesión.
    facturasSIIChile: facturasSIIPorPais.Chile || VACIO,
    facturasSIIPeru: facturasSIIPorPais["Perú"] || VACIO,
    facturasExternasChile: facturasExternasPorPais.Chile || VACIO,
    facturasExternasPeru: facturasExternasPorPais["Perú"] || VACIO,
    boletasHonorariosChile: boletasHonorariosPorPais.Chile || VACIO,
    boletasHonorariosPeru: boletasHonorariosPorPais["Perú"] || VACIO,
    inversionMensual: inversionPorPais[pais] || CERO12(),
    inversionMensualChile: inversionPorPais.Chile || CERO12(),
    inversionMensualPeru: inversionPorPais["Perú"] || CERO12(),
    valorActivosMensual: valorActivosPorPais[pais] || CERO12(),
    valorActivosMensualChile: valorActivosPorPais.Chile || CERO12(),
    valorActivosMensualPeru: valorActivosPorPais["Perú"] || CERO12(),
    empleadosRRHHChile: empleadosPorPais.Chile || [],
    empleadosRRHHPeru: empleadosPorPais["Perú"] || [],
    manual,
    dtesSinVincular,
    siiVinculacionesMap,
    // El modo combinado del v1 exige tener los datos de ambos países cargados.
    datosCombinadosCargados: Boolean(porPais.Chile && porPais["Perú"]),
    isLoading,
  };
};
